import { z } from 'zod';
import { nullableList } from '../zod-helpers';

const uuid = z.string().uuid();

export const moderationStateSchema = z.enum(['open', 'triaged', 'resolved']);
export type ModerationState = z.infer<typeof moderationStateSchema>;

export const subjectKindSchema = z.enum([
  'post',
  'comment',
  'user',
  'story',
  'event',
  'group',
  'announcement',
  'review',
  'product',
]);
export type SubjectKind = z.infer<typeof subjectKindSchema>;

/** GET /admin/v1/moderation/queue */
export const moderationReportSchema = z.object({
  id: uuid,
  reporter_id: uuid.nullable().optional(),
  subject_kind: z.string(),
  subject_id: uuid,
  category: z.string(),
  state: z.string(),
  sla_due_at: z.string().nullable().optional(),
  created_at: z.string(),
});
export type ModerationReport = z.infer<typeof moderationReportSchema>;
export const moderationQueueResponseSchema = nullableList(moderationReportSchema);

export const moderationActionSchema = z.enum([
  'warn',
  'hide',
  'remove',
  'shadow_limit',
  'suspend_temp',
  'suspend_perm',
  'restore',
  'escalate',
]);
export type ModerationAction = z.infer<typeof moderationActionSchema>;

/** POST /admin/v1/moderation/actions */
export const moderationActionRequestSchema = z
  .object({
    subject_kind: z.string().min(1),
    subject_id: uuid,
    action: moderationActionSchema,
    reason: z.string().min(3).max(500),
    duration_hours: z.number().int().positive().max(24 * 365).optional(),
    policy_rule_id: uuid.optional(),
    report_id: uuid.optional(),
  })
  .refine(
    (v) => (v.action === 'suspend_temp' ? typeof v.duration_hours === 'number' : true),
    { message: 'duration_hours required for suspend_temp', path: ['duration_hours'] }
  );
export type ModerationActionRequest = z.infer<typeof moderationActionRequestSchema>;

export const moderationActionResponseSchema = z.object({ id: uuid });
export type ModerationActionResponse = z.infer<typeof moderationActionResponseSchema>;

/** POST /admin/v1/appeals/{id}/resolve */
export const appealResolveSchema = z.object({
  state: z.enum(['upheld', 'overturned']),
  notes: z.string().max(2000).optional(),
});
export type AppealResolve = z.infer<typeof appealResolveSchema>;
